import { apiLookupEmail, apiCheckTenant, LookupEmailResponse } from "@/services/workspace.api";
import { isSAWDomain, isSAWPlan } from "@/utils";
import { Button, Form, Input } from "antd";
import { debounce } from "lodash";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface EmailFormValues {
    email: string;
}

export function Email() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState<boolean>(false);
    const resultRef = useRef<LookupEmailResponse>();

    const [form] = Form.useForm<EmailFormValues>();

    const triggerRealm = useCallback(async () => {
        const res = await form.validateFields();
        if (resultRef.current?.domain) {
            const uri = new URL(resultRef.current?.domain);
            uri.searchParams.set('useremail', window.btoa(res.email));
            uri.searchParams.set('tenant_plan_category', resultRef.current.tenant_plan_category);
            window.location.href = uri.href;
        }
    }, [form]);

    const onChange = debounce((event: any) => {
        const value = event.target.value;

        if (value) {
            setLoading(true);

            return form.validateFields().then(() => {
                setLoading(false);
            }).catch(() => {
                setLoading(false);
                return Promise.resolve();
            });
        }
    }, 300);

    function checkOrganization(email: string) {
        return apiLookupEmail(email);
    }

    const handleSubmit = useCallback(async () => {
        try {
            setLoading(true);
            await triggerRealm();
        } finally {
            setLoading(false);
        }
	}, [triggerRealm]);

    return (
        <Form form={form}>
            <Form.Item
                hasFeedback
                name="email"
                rules={[{
                    required: true,
                    message: t('workspace.email.placeholder'),
                }, {
                    validator: async (_, value) => {
                        if (value) {
                            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                                return Promise.reject(new Error(t('workspace.email.placeholder')));
                            }

                            value = value.trim();

                            return checkOrganization(value).then((res: any) => {
                                if (res.code === 0) {
                                    resultRef.current = res.data;

                                    return Promise.resolve();
                                } else if (res.code === 600002) {
                                    return Promise.reject(new Error(t('eamil.teams.login')));
                                }

                                return Promise.reject(new Error(res.message || t("email.invalid")));
                            }).catch((error) => {
                                if (!error.message) {
                                    return Promise.reject(new Error(t("email.invalid")));
                                }

                                return Promise.reject(error);
                            });
                        }
                        return Promise.resolve();
                    },
                    validateTrigger: 'onSubmit',
                }]}
            >
                <Input
                    onChange={onChange}
                    size="large"
                    placeholder={t('workspace.email.placeholder')}
                />
            </Form.Item>
            <Form.Item>
                <Button
                    block
                    type="primary"
                    size="large"
                    onClick={handleSubmit}
                    loading={loading}
                >
                    {t('workspace.continue')}
                </Button>
            </Form.Item>
        </Form>
    )
}
